/* ═══════════════════════════════════════════════════════════════
   ADAPTADOR LEGACY — adminHosts
   Lee del motor canónico persons.ts y exporta en el formato
   antiguo esperado por la página /admin/hosts.
   ═══════════════════════════════════════════════════════════════ */

import { canonicalPersons, getHosts } from './persons';
import type { CanonicalPerson } from './persons';

/* ─── Tipos legacy (sin cambios) ─── */
export type HostStatus = 'on_air' | 'active' | 'inactive';

export interface HostProgram {
  name: string;
  schedule: string;
}

export interface Host {
  id: number;
  name: string;
  username: string;
  photoUrl: string;
  bio: string;
  programs: HostProgram[];
  followers: number;
  interactions: {
    likes: number;
    comments: number;
    shares: number;
  };
  totalInteractions: number;
  status: HostStatus;
  monthlyRevenue: number;
  projectedRevenue: number;
  joinedAt: string;
}

export const hostStatusLabels: Record<HostStatus, string> = {
  on_air: 'Al aire',
  active: 'Activo',
  inactive: 'Inactivo',
};

export const hostStatusColors: Record<HostStatus, string> = {
  on_air: 'bg-red-100 text-red-700 border-red-200',
  active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  inactive: 'bg-foreground-200 text-foreground-600 border-foreground-300',
};

export const hostStatusDotColors: Record<HostStatus, string> = {
  on_air: 'bg-red-500',
  active: 'bg-emerald-500',
  inactive: 'bg-foreground-400',
};

/* ─── Programa → mapeo nombre-display a schedule ─── */
const programScheduleMap: Record<string, string> = {
  'el-mananero': 'Lun-Vie 06:00-10:00',
  'top-40-countdown': 'Sáb 14:00-17:00',
  'la-hora-pico': 'Lun-Vie 16:00-18:00',
  'beats-and-bytes': 'Jue 21:00-23:00',
  'zona-deportiva': 'Lun-Vie 18:00-20:00',
  'entrevistas-exclusivas': 'Mar-Jue 11:00-12:00',
  'sesiones-acusticas': 'Mié 20:00-22:00',
  'noticias-al-instante': 'Lun-Vie 12:00-13:00',
  'noticias-al-dia': 'Lun-Vie 12:00-13:00',
  'debate-abierto': 'Mié 19:00-20:00',
  'urban-beats': 'Sáb 18:00-21:00',
  'trend-alert': 'Lun-Mié 14:00-15:00',
  'cultura-viva': 'Dom 10:00-12:00',
  'despierta-con-energia': 'Lun-Vie 06:00-10:00',
  'domingo-familiar': 'Dom 10:00-14:00',
  'miercoles-de-conciertos': 'Mié 20:00-23:00',
  'noche-de-electronica': 'Sáb 20:00-23:00',
  'deportes-de-noche': 'Jue 20:00-22:00',
  'noches-de-jazz': 'Lun-Vie 21:00-23:00',
  'noticiero-central': 'Lun-Vie 12:00-13:00',
  'fin-de-semana-mix': 'Sáb-Dom 10:00-14:00',
  'deportivo-express': 'Lun-Vie 18:00-19:00',
};

const programDisplayNameMap: Record<string, string> = {
  'el-mananero': 'El Mañanero',
  'top-40-countdown': 'Top 40 Countdown',
  'la-hora-pico': 'La Hora Pico',
  'beats-and-bytes': 'Beats & Bytes',
  'zona-deportiva': 'Zona Deportiva',
  'entrevistas-exclusivas': 'Entrevistas Exclusivas',
  'sesiones-acusticas': 'Sesiones Acústicas',
  'noticias-al-instante': 'Noticias al Instante',
  'noticias-al-dia': 'Noticias Al Día',
  'debate-abierto': 'Debate Abierto',
  'urban-beats': 'Urban Beats',
  'trend-alert': 'Trend Alert',
  'cultura-viva': 'Cultura Viva',
  'despierta-con-energia': 'Despierta con Energía',
  'domingo-familiar': 'Domingo Familiar',
  'miercoles-de-conciertos': 'Miércoles de Conciertos',
  'noche-de-electronica': 'Noche de Electrónica',
  'deportes-de-noche': 'Deportes de Noche',
  'noches-de-jazz': 'Noches de Jazz',
  'noticiero-central': 'Noticiero Central',
  'fin-de-semana-mix': 'Fin de Semana Mix',
  'deportivo-express': 'Deportivo Express',
};

function personToHost(person: CanonicalPerson, index: number): Host {
  const hp = person.hostProfile!;
  const programs: HostProgram[] = hp.programs.map((slug) => ({
    name: programDisplayNameMap[slug] || slug,
    schedule: programScheduleMap[slug] || '',
  }));

  return {
    id: index + 1,  // mantener IDs 1-8 para compatibilidad
    name: person.displayName,
    username: hp.username,
    photoUrl: person.avatar,
    bio: person.bio,
    programs,
    followers: hp.followers,
    interactions: hp.interactions,
    totalInteractions: hp.interactions.likes + hp.interactions.comments + hp.interactions.shares,
    status: hp.hostStatus,
    monthlyRevenue: hp.monthlyRevenue,
    projectedRevenue: hp.projectedRevenue,
    joinedAt: hp.joinedAt,
  };
}

/* ─── Los 8 hosts legacy, ahora desde el motor canónico ─── */
const legacyHostPersonIds = ['p-002', 'p-003', 'p-004', 'p-005', 'p-008', 'p-009', 'p-006', 'p-007'];

function buildStationHosts(): Host[] {
  return legacyHostPersonIds
    .map((id, idx) => {
      const person = canonicalPersons.find((p) => p.id === id);
      if (!person || !person.hostProfile) return null;
      return personToHost(person, idx);
    })
    .filter(Boolean) as Host[];
}

export const stationHosts: Host[] = buildStationHosts();

export function getStationHosts(_stationId: number): Host[] {
  return stationHosts;
}